import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import {
  PROGRAMA_NOVEDADES_NOMINA,
  puedeVerProgramaNominaPropio,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx, fpDataGridSx } from 'components/financepro';

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const cardShellSx = {
  borderRadius: 2.5,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  border: `1px solid ${FP.color.border}`,
  overflow: 'hidden',
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const fmtMoney = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v || '').replace(/,/g, ''));
  if (Number.isNaN(n)) return '0.00';
  return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: '100%', py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin registros de nómina
      </Typography>
    </Stack>
  );
}

const moneyCol = (field, headerName, width = 100) => ({
  field,
  headerName,
  width,
  align: 'right',
  headerAlign: 'right',
  valueFormatter: (value) => fmtMoney(value),
});

const columnsDetallada = [
  { field: 'num_emp', headerName: 'NUM', width: 70 },
  { field: 'nombres', headerName: 'NOMBRES', flex: 1, minWidth: 180 },
  { field: 'horas_norm', headerName: 'H.NORM', width: 80, align: 'right', headerAlign: 'right', valueFormatter: (value) => fmtMoney(value) },
  { field: 'horas_35', headerName: '35%', width: 70, align: 'right', headerAlign: 'right', valueFormatter: (value) => fmtMoney(value) },
  { field: 'horas_100', headerName: '100%', width: 70, align: 'right', headerAlign: 'right', valueFormatter: (value) => fmtMoney(value) },
  { field: 'horas_fer', headerName: 'FER', width: 70, align: 'right', headerAlign: 'right', valueFormatter: (value) => fmtMoney(value) },
  moneyCol('pago_norm', 'NORMAL', 100),
  moneyCol('pago_35', '35%', 90),
  moneyCol('pago_100', 'FERIADO', 100),
  moneyCol('otros_ing', 'OTROS ING', 100),
  moneyCol('bruto', 'BRUTO', 110),
  moneyCol('isr', 'ISR', 90),
  moneyCol('sfs', 'SFS', 90),
  moneyCol('afp', 'AFP', 90),
  moneyCol('otros_des', 'OTROS DES', 100),
  moneyCol('neto', 'NETO', 110),
];

const columnsResumida = [
  { field: 'num_emp', headerName: 'NUM', width: 80 },
  { field: 'nombres', headerName: 'NOMBRES', flex: 1, minWidth: 200 },
  moneyCol('pago_norm', 'NORMAL', 110),
  moneyCol('feriado', 'FERIADO', 110),
  moneyCol('otros_ing', 'OTROS ING', 110),
  moneyCol('bruto', 'BRUTO', 120),
  moneyCol('isr', 'ISR', 100),
  moneyCol('sfs', 'SFS', 100),
  moneyCol('afp', 'AFP', 100),
  moneyCol('otros_des', 'OTROS DES', 110),
  moneyCol('neto', 'NETO', 120),
];

function sumar(lista, campo) {
  return lista.reduce((acc, e) => acc + (Number(e[campo]) || 0), 0);
}

const CAMPOS_INGRESOS = Array.from({ length: 12 }, (_, i) => `ing_${String(i + 1).padStart(2, '0')}`);
const CAMPOS_DEDUCCIONES = Array.from({ length: 12 }, (_, i) => `ded_${String(i + 1).padStart(2, '0')}`);

function monto(valor) {
  const n = Number(valor);
  return Number.isFinite(n) ? n : 0;
}

function redondear2(valor) {
  return Math.round(monto(valor) * 100) / 100;
}

function sumarCampos(emp, campos) {
  return campos.reduce((acc, campo) => acc + monto(emp?.[campo]), 0);
}

function conTotalesNomina(emp) {
  const ingresos = sumarCampos(emp, CAMPOS_INGRESOS);
  const deducciones = sumarCampos(emp, CAMPOS_DEDUCCIONES);
  const bruto =
    monto(emp.pago_norm) +
    monto(emp.pago_15) +
    monto(emp.pago_35) +
    monto(emp.pago_50) +
    monto(emp.pago_100) +
    ingresos;
  const neto = bruto - monto(emp.isr) - monto(emp.sfs) - monto(emp.afp) - deducciones;
  return {
    ...emp,
    otros_ing: redondear2(ingresos),
    otros_des: redondear2(deducciones),
    bruto: redondear2(bruto),
    neto: redondear2(neto),
  };
}

function agruparPorDepto(empleados) {
  const mapa = {};
  const orden = [];
  for (const emp of empleados) {
    const key = emp.cod_super || '—';
    if (!mapa[key]) {
      mapa[key] = [];
      orden.push(key);
    }
    mapa[key].push(emp);
  }
  return { mapa, orden };
}

const GenerarNomina = ({ onVolver }) => {
  const { controller, selectedCompany, showError } = useProductosMaterialContext();
  const { usuarioLog, roleDetalles, nombreNegocio } = controller;
  const { post } = useApi();

  const puedeLeer = useMemo(
    () => puedeVerProgramaNominaPropio(roleDetalles, PROGRAMA_NOVEDADES_NOMINA),
    [roleDetalles],
  );
  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [ano, setAno] = useState('');
  const [numero, setNumero] = useState('');
  const [planta, setPlanta] = useState('');
  const [departamento, setDepartamento] = useState(null);
  const [departamentos, setDepartamentos] = useState([]);
  const [modo, setModo] = useState('detallada');
  const [cargando, setCargando] = useState(false);
  const [procesado, setProcesado] = useState(false);
  const [resultado, setResultado] = useState(null);

  const tablaRef = useRef(null);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany],
  );

  useEffect(() => {
    if (!puedeLeer || !puedeOperar) return;
    (async () => {
      try {
        const [resPeriodo, resDepto] = await Promise.all([
          post('/api/nomina_obtenerperiodo', credencialesEmpresa()),
          post('/api/nomina_listardepartamentos', credencialesEmpresa()),
        ]);
        if (resPeriodo.ok && resPeriodo.data?.periodo) {
          setAno(String(resPeriodo.data.periodo.ano || ''));
          setNumero(String(resPeriodo.data.periodo.numero || ''));
        }
        if (resDepto.ok && Array.isArray(resDepto.data?.departamentos)) {
          setDepartamentos(
            resDepto.data.departamentos.map((d) => ({
              id: String(d.id),
              label: `${d.id}-${(d.descrip || '').trim()}`,
            })),
          );
        }
      } catch {
        /* silencioso */
      }
    })();
  }, [puedeLeer, puedeOperar, post, credencialesEmpresa]);

  const procesar = async () => {
    if (!ano || !numero) {
      showError('Año y número de nómina son requeridos');
      return;
    }
    setCargando(true);
    setProcesado(false);
    try {
      const { ok, data } = await post('/api/nomina_generarnomina', {
        ...credencialesEmpresa(),
        ano,
        numero,
        planta,
        departamento: departamento?.id || '',
      });
      if (!ok) throw new Error(data.Error || 'Error al generar la nómina');
      setResultado(data);
      setProcesado(true);
    } catch (e) {
      showError(e.message || 'Error al generar la nómina');
    } finally {
      setCargando(false);
    }
  };

  const empleados = useMemo(
    () => (resultado?.empleados || []).map(conTotalesNomina),
    [resultado],
  );
  const rows = useMemo(
    () => empleados.map((e) => ({ ...e, id: e.num_emp })),
    [empleados],
  );
  const esDetallada = modo === 'detallada';

  const imprimir = () => {
    if (!tablaRef.current) return;
    const contenido = tablaRef.current.innerHTML;
    const ventana = window.open('', '_blank', 'width=1100,height=700');
    ventana.document.write(`
      <html>
        <head>
          <title>Nómina ${ano}-${numero}</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 11px; margin: 16px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 3px 5px; text-align: right; border-bottom: 1px solid #ccc; }
            th { background: #eee; font-weight: bold; text-align: right; }
            td.left, th.left { text-align: left; }
            .encabezado { text-align: center; font-weight: bold; margin-bottom: 4px; }
            .subtotal td { font-weight: bold; border-top: 2px solid #333; background: #f4f4f4; }
            .total td { font-weight: bold; border-top: 3px double #333; background: #e8e8e8; }
            @media print { body { margin: 0; } }
            @page { size: landscape; margin: 10mm; }
          </style>
        </head>
        <body>${contenido}</body>
      </html>
    `);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => {
      ventana.print();
    }, 400);
  };

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Generar Nómina" subtitle="Nómina" />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64, height: 64, borderRadius: 1.6, mx: 'auto', mb: 2.5,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: FP.color.dangerSoft, color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: FP.color.textPrimary, mb: 1 }}>
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem' }}>
              Su rol no tiene acceso a esta opción.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  const { mapa, orden } = agruparPorDepto(empleados);

  const renderTablaImpresion = () => {
    if (!procesado || !empleados.length) return null;
    const encabezado = (
      <div>
        <div className="encabezado" style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 4 }}>
          {resultado.nombre_empresa}
        </div>
        <div className="encabezado" style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 4 }}>
          Nómina General #{numero} Desde {resultado.periodo?.fecha1} hasta {resultado.periodo?.fecha2}
        </div>
      </div>
    );

    const fmt = (v) => {
      const n = Number(v);
      if (!n) return '0.00';
      return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    if (esDetallada) {
      return (
        <div>
          {encabezado}
          <table>
            <thead>
              <tr>
                <th className="left">NUM</th>
                <th className="left">NOMBRES</th>
                <th>H.NORM</th>
                <th>35%</th>
                <th>100%</th>
                <th>FER</th>
                <th>NORMAL</th>
                <th>35%</th>
                <th>FERIADO</th>
                <th>OTROS ING</th>
                <th>BRUTO</th>
                <th>ISR</th>
                <th>SFS</th>
                <th>AFP</th>
                <th>OTROS DES</th>
                <th>NETO</th>
              </tr>
            </thead>
            <tbody>
              {orden.map((depto) => {
                const lista = mapa[depto];
                return (
                  <React.Fragment key={depto}>
                    {lista.map((e) => (
                      <tr key={e.num_emp}>
                        <td className="left">{e.num_emp}</td>
                        <td className="left">{(e.nombres || '').substring(0, 35)}</td>
                        <td>{fmt(e.horas_norm)}</td>
                        <td>{fmt(e.horas_35)}</td>
                        <td>{fmt(e.horas_100)}</td>
                        <td>{fmt(e.horas_fer)}</td>
                        <td>{fmt(e.pago_norm)}</td>
                        <td>{fmt(e.pago_35)}</td>
                        <td>{fmt(e.pago_100)}</td>
                        <td>{fmt(e.otros_ing)}</td>
                        <td>{fmt(e.bruto)}</td>
                        <td>{fmt(e.isr)}</td>
                        <td>{fmt(e.sfs)}</td>
                        <td>{fmt(e.afp)}</td>
                        <td>{fmt(e.otros_des)}</td>
                        <td>{fmt(e.neto)}</td>
                      </tr>
                    ))}
                    <tr className="subtotal">
                      <td className="left">TOT</td>
                      <td className="left">DEPTO {depto} ({lista.length} Emp)</td>
                      <td />
                      <td />
                      <td />
                      <td />
                      <td>{fmt(sumar(lista, 'pago_norm'))}</td>
                      <td>{fmt(sumar(lista, 'pago_35'))}</td>
                      <td>{fmt(sumar(lista, 'pago_100'))}</td>
                      <td>{fmt(sumar(lista, 'otros_ing'))}</td>
                      <td>{fmt(sumar(lista, 'bruto'))}</td>
                      <td>{fmt(sumar(lista, 'isr'))}</td>
                      <td>{fmt(sumar(lista, 'sfs'))}</td>
                      <td>{fmt(sumar(lista, 'afp'))}</td>
                      <td>{fmt(sumar(lista, 'otros_des'))}</td>
                      <td>{fmt(sumar(lista, 'neto'))}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
              <tr className="total">
                <td className="left">TOT</td>
                <td className="left">Empleados {empleados.length}</td>
                <td />
                <td />
                <td />
                <td />
                <td>{fmt(sumar(empleados, 'pago_norm'))}</td>
                <td>{fmt(sumar(empleados, 'pago_35'))}</td>
                <td>{fmt(sumar(empleados, 'pago_100'))}</td>
                <td>{fmt(sumar(empleados, 'otros_ing'))}</td>
                <td>{fmt(sumar(empleados, 'bruto'))}</td>
                <td>{fmt(sumar(empleados, 'isr'))}</td>
                <td>{fmt(sumar(empleados, 'sfs'))}</td>
                <td>{fmt(sumar(empleados, 'afp'))}</td>
                <td>{fmt(sumar(empleados, 'otros_des'))}</td>
                <td>{fmt(sumar(empleados, 'neto'))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div>
        {encabezado}
        <table>
          <thead>
            <tr>
              <th className="left">NUM</th>
              <th className="left">NOMBRES</th>
              <th>NORMAL</th>
              <th>FERIADO</th>
              <th>OTROS ING</th>
              <th>BRUTO</th>
              <th>ISR</th>
              <th>SFS</th>
              <th>AFP</th>
              <th>OTROS DES</th>
              <th>NETO</th>
            </tr>
          </thead>
          <tbody>
            {orden.map((depto) => {
              const lista = mapa[depto];
              return (
                <React.Fragment key={depto}>
                  {lista.map((e) => (
                    <tr key={e.num_emp}>
                      <td className="left">{e.num_emp}</td>
                      <td className="left">{(e.nombres || '').substring(0, 25)}</td>
                      <td>{fmt(e.pago_norm)}</td>
                      <td>{fmt(e.feriado)}</td>
                      <td>{fmt(e.otros_ing)}</td>
                      <td>{fmt(e.bruto)}</td>
                      <td>{fmt(e.isr)}</td>
                      <td>{fmt(e.sfs)}</td>
                      <td>{fmt(e.afp)}</td>
                      <td>{fmt(e.otros_des)}</td>
                      <td>{fmt(e.neto)}</td>
                    </tr>
                  ))}
                  <tr className="subtotal">
                    <td className="left">TOT</td>
                    <td className="left">DEPTO {depto} ({lista.length} Emp)</td>
                    <td>{fmt(sumar(lista, 'pago_norm'))}</td>
                    <td>{fmt(sumar(lista, 'feriado'))}</td>
                    <td>{fmt(sumar(lista, 'otros_ing'))}</td>
                    <td>{fmt(sumar(lista, 'bruto'))}</td>
                    <td>{fmt(sumar(lista, 'isr'))}</td>
                    <td>{fmt(sumar(lista, 'sfs'))}</td>
                    <td>{fmt(sumar(lista, 'afp'))}</td>
                    <td>{fmt(sumar(lista, 'otros_des'))}</td>
                    <td>{fmt(sumar(lista, 'neto'))}</td>
                  </tr>
                </React.Fragment>
              );
            })}
            <tr className="total">
              <td className="left">TOT</td>
              <td className="left">Empleados {empleados.length}</td>
              <td>{fmt(sumar(empleados, 'pago_norm'))}</td>
              <td>{fmt(sumar(empleados, 'feriado'))}</td>
              <td>{fmt(sumar(empleados, 'otros_ing'))}</td>
              <td>{fmt(sumar(empleados, 'bruto'))}</td>
              <td>{fmt(sumar(empleados, 'isr'))}</td>
              <td>{fmt(sumar(empleados, 'sfs'))}</td>
              <td>{fmt(sumar(empleados, 'afp'))}</td>
              <td>{fmt(sumar(empleados, 'otros_des'))}</td>
              <td>{fmt(sumar(empleados, 'neto'))}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Listar Nómina"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Generación de nómina`
            : 'Generación de nómina'
        }
        cta={
          onVolver ? (
            <Button startIcon={<ArrowBackOutlinedIcon />} onClick={onVolver} sx={btnOutline(FP.color.navy)}>
              Volver
            </Button>
          ) : null
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Año"
                value={ano}
                onChange={(e) => setAno(e.target.value.replace(/\D/g, ''))}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Nómina"
                value={numero}
                onChange={(e) => setNumero(e.target.value.replace(/\D/g, ''))}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Planta"
                value={planta}
                onChange={(e) => setPlanta(e.target.value)}
                placeholder="(opcional)"
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                options={departamentos}
                value={departamento}
                onChange={(_, v) => setDepartamento(v)}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Departamento"
                    placeholder="(opcional)"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={modo}
              exclusive
              onChange={(_, v) => { if (v) setModo(v); }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  px: 2,
                  borderColor: FP.color.border,
                  color: FP.color.textSecondary,
                },
                '& .Mui-selected': {
                  bgcolor: `${FP.color.navy}14 !important`,
                  color: `${FP.color.navy} !important`,
                  borderColor: `${FP.color.navy} !important`,
                },
              }}
            >
              <ToggleButton value="detallada">Detallada</ToggleButton>
              <ToggleButton value="resumida">Resumida</ToggleButton>
            </ToggleButtonGroup>

            <Button
              onClick={procesar}
              disabled={cargando || !puedeOperar}
              startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : null}
              sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
            >
              {cargando ? 'Procesando…' : 'Procesar'}
            </Button>

            {procesado && rows.length > 0 && (
              <Button
                onClick={imprimir}
                startIcon={<PrintOutlinedIcon />}
                sx={btnOutline(FP.color.navy)}
              >
                Imprimir
              </Button>
            )}

            {procesado && (
              <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.85rem', ml: 'auto' }}>
                {rows.length} empleados
              </Typography>
            )}
          </Box>

          {procesado && rows.length > 0 && (
            <>
              <Typography
                sx={{
                  textAlign: 'center',
                  fontWeight: 800,
                  fontSize: '1rem',
                  color: FP.color.textPrimary,
                  mb: 0.5,
                }}
              >
                {resultado?.nombre_empresa}
              </Typography>
              <Typography
                sx={{
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  color: FP.color.textSecondary,
                  mb: 2,
                }}
              >
                Nómina General #{numero} · {resultado?.periodo?.fecha1} al{' '}
                {resultado?.periodo?.fecha2}
              </Typography>
            </>
          )}

          <Box sx={{ bgcolor: '#fff' }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={esDetallada ? columnsDetallada : columnsResumida}
              loading={cargando}
              rowHeight={40}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, '--DataGrid-overlayHeight': '220px' }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Tabla oculta usada para impresión */}
      <Box ref={tablaRef} sx={{ display: 'none' }}>
        {procesado && renderTablaImpresion()}
      </Box>
    </Box>
  );
};

export default GenerarNomina;
